"use client";

import Logo from "@/base/component/logo";
import { appConfig } from "@/config";
import Link from "next/link";
import { useMemo } from "react";

export default function Footer() {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer className="static block w-full bg-background border-t border-divider mt-16">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="flex flex-col gap-4">
            <Logo />

            <p className="text-sm">
              Turn websites into structured CSV and JSON data with dynamic API
              endpoint, visually!
            </p>

            <Link href="/" className="text-sm">
              Home
            </Link>
            <Link href="/glossary" className="text-sm">
              Glossary
            </Link>
            <Link href="/contact" className="text-sm">
              Contact
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            <span className="text-xl">Projects</span>
            <Link
              href="https://contextch.at"
              target="_blank"
              className="text-sm"
              title="ContextChat - Chat with Context from Web, Files and GitHub"
              rel="noopener"
            >
              ContextChat
            </Link>
            <Link
              href="https://apifrom.com"
              target="_blank"
              className="text-sm"
              title="ApiFrom - Turn websites into structured CSV and JSON data with dynamic API endpoint, visually!"
              rel="noopener"
            >
              ApiFrom
            </Link>
            <Link
              href="https://memoanki.com"
              target="_blank"
              className="text-sm"
              title="MemoAnki - Memorization and flashcard learning system"
              rel="noopener"
            >
              MemoAnki
            </Link>
            <Link
              href="https://ttclubmanager.com"
              target="_blank"
              className="text-sm"
              title="TT Club Manager - Table Tennis club management software"
              rel="noopener"
            >
              TT Club Manager
            </Link>
            <Link
              href="https://ev-charge-calculator.com"
              target="_blank"
              className="text-sm"
              title="EV Charge Calculator - Electric vehicle charging cost calculator"
              rel="noopener"
            >
              EV Charge Calculator
            </Link>
            <Link
              href="https://pic.events"
              target="_blank"
              className="text-sm"
              title="Pic Events - Event photography platform"
              rel="noopener"
            >
              Pic Events
            </Link>
            <Link
              href="https://woodybooth.it"
              target="_blank"
              className="text-sm"
              title="Woody Booth - Premium photo booth rental services"
              rel="noopener"
            >
              WoodyBooth
            </Link>
          </div>

          <div className="flex flex-col gap-3 static">
            <span className="text-xl">Legal</span>
            <Link
              href="/docs/privacy-policy/index.html"
              target="_blank"
              className="text-sm static"
            >
              Privacy
            </Link>
            <Link
              href="/docs/terms-of-service/index.html"
              target="_blank"
              className="text-sm"
            >
              Terms
            </Link>
            <Link
              href="/docs/cookie-policy/index.html"
              target="_blank"
              className="text-sm"
            >
              Cookie Policy
            </Link>
            <Link href="https://ricu.it" target="_blank" className="text-sm">
              About me
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            <span className="text-xl">Integrations</span>
            <Link
              href="https://dataforseo.com"
              target="_blank"
              className="text-sm"
            >
              DataForSEO
            </Link>
            <Link href="https://openai.com" target="_blank" className="text-sm">
              OpenAI
            </Link>
            <Link
              href="https://gemini.google.com/"
              target="_blank"
              className="text-sm"
            >
              Google Gemini
            </Link>
          </div>
        </div>

        <div className="mt-2 pt-6">
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm font-medium">Connect</p>
            <div className="flex gap-4">
              <Link
                href="https://x.com/MatteoRicupero"
                aria-label="Follow me on Twitter"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </Link>
              <Link
                href="https://github.com/tapeo"
                aria-label="View my GitHub profile"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
              <Link
                href="https://www.linkedin.com/in/matteo-ricupero-707abb121/"
                aria-label="Connect with me on LinkedIn"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </Link>
              <Link
                href="https://www.tiktok.com/@seoaipal.com"
                target="_blank"
                className="hover:opacity-70"
                aria-label="Follow us on TikTok"
              >
                <span className="sr-only">TikTok</span>
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-6 text-center">
          <p className="text-sm opacity-70">
            © {currentYear} {appConfig.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
